import React, { useState, useEffect, useRef } from 'react';
import './ExamInterface.css';

const DEMO_QUESTIONS = [
  {
    id: 1,
    question: "What is the time complexity of binary search?",
    options: [
      "O(n)",
      "O(log n)",
      "O(n²)",
      "O(1)"
    ],
    correctAnswer: 1
  },
  {
    id: 2,
    question: "Which data structure uses LIFO (Last In First Out)?",
    options: [
      "Queue",
      "Stack",
      "Array",
      "Linked List"
    ],
    correctAnswer: 1
  },
  {
    id: 3,
    question: "What does SQL stand for?",
    options: [
      "Structured Query Language",
      "Simple Question Language",
      "Standard Query Logic",
      "System Query Language"
    ],
    correctAnswer: 0
  },
  {
    id: 4,
    question: "Which of the following is NOT a programming paradigm?",
    options: [
      "Object-Oriented",
      "Functional",
      "Procedural",
      "Sequential"
    ],
    correctAnswer: 3
  },
  {
    id: 5,
    question: "What is the purpose of a constructor in OOP?",
    options: [
      "To destroy objects",
      "To initialize objects",
      "To copy objects",
      "To compare objects"
    ],
    correctAnswer: 1
  }
];

function ExamInterface({ examId, studentId, sessionId, violations, onEndExam }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes
  const [showWarning, setShowWarning] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Start camera for PIP view
    startCamera();
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480 } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera access denied:', err);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers({
      ...answers,
      [questionId]: answerIndex
    });
  };

  const handleNext = () => {
    if (currentQuestion < DEMO_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitExam = () => {
    const score = calculateScore();
    alert(`Exam Submitted!\n\nYour Score: ${score}/${DEMO_QUESTIONS.length}\nPercentage: ${((score/DEMO_QUESTIONS.length)*100).toFixed(1)}%`);
    onEndExam();
  };

  const calculateScore = () => {
    let score = 0;
    DEMO_QUESTIONS.forEach(q => {
      if (answers[q.id] === q.correctAnswer) {
        score++;
      }
    });
    return score;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length;
  };

  const question = DEMO_QUESTIONS[currentQuestion];

  return (
    <div className="exam-interface">
      {/* Picture-in-Picture Webcam View */}
      <div className="pip-camera">
        <video ref={videoRef} autoPlay muted />
        <div className="pip-status">
          <div className="pip-indicator"></div>
          Monitoring
        </div>
      </div>

      <div className="exam-header">
        <div className="exam-info">
          <h2>📝 {examId}</h2>
          <p>Student: {studentId}</p>
        </div>
        <div className="exam-timer">
          <div className={`timer ${timeLeft < 300 ? 'timer-warning' : ''}`}>
            ⏱️ {formatTime(timeLeft)}
          </div>
          <button className="cancel-exam-btn" onClick={() => setShowCancelWarning(true)}>
            ✕ Cancel Exam
          </button>
        </div>
      </div>

      <div className="exam-content">
        <div className="question-sidebar">
          <h3>Questions</h3>
          <div className="question-grid">
            {DEMO_QUESTIONS.map((q, idx) => (
              <button
                key={q.id}
                className={`question-nav-btn ${
                  idx === currentQuestion ? 'active' : ''
                } ${answers[q.id] !== undefined ? 'answered' : ''}`}
                onClick={() => setCurrentQuestion(idx)}
              >
                {idx + 1}
              </button>
            ))}
          </div>
          <div className="progress-info">
            <p>Answered: {getAnsweredCount()}/{DEMO_QUESTIONS.length}</p>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${(getAnsweredCount()/DEMO_QUESTIONS.length)*100}%` }}
              />
            </div>
          </div>
          
          {violations && violations.length > 0 && (
            <div className="sidebar-violations">
              <h4>⚠️ Violations: {violations.length}</h4>
              <div className="violation-summary">
                {violations.slice(-2).reverse().map((v, idx) => (
                  <div key={idx} className={`mini-violation ${v.severity.toLowerCase()}`}>
                    {v.type.replace(/_/g, ' ')}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="question-area">
          <div className="question-header">
            <span className="question-number">Question {currentQuestion + 1} of {DEMO_QUESTIONS.length}</span>
            {answers[question.id] !== undefined && (
              <span className="answered-badge">✓ Answered</span>
            )}
          </div>

          <div className="question-text">
            {question.question}
          </div>

          <div className="options-container">
            {question.options.map((option, idx) => (
              <label
                key={idx}
                className={`option ${answers[question.id] === idx ? 'selected' : ''}`}
              >
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  checked={answers[question.id] === idx}
                  onChange={() => handleAnswerSelect(question.id, idx)}
                />
                <span className="option-label">{String.fromCharCode(65 + idx)}</span>
                <span className="option-text">{option}</span>
              </label>
            ))}
          </div>

          <div className="navigation-buttons">
            <button
              className="nav-btn secondary"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
            >
              ← Previous
            </button>
            
            {currentQuestion === DEMO_QUESTIONS.length - 1 ? (
              <button
                className="nav-btn submit"
                onClick={() => setShowWarning(true)}
              >
                Submit Exam
              </button>
            ) : (
              <button
                className="nav-btn primary"
                onClick={handleNext}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </div>

      {showWarning && (
        <div className="modal-overlay" onClick={() => setShowWarning(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Submit Exam?</h3>
            <p>
              You have answered {getAnsweredCount()} out of {DEMO_QUESTIONS.length} questions.
            </p>
            <p>Are you sure you want to submit your exam?</p>
            {violations && violations.length > 0 && (
              <p className="violation-warning">
                ⚠️ Note: {violations.length} violation(s) recorded during this exam.
              </p>
            )}
            <div className="modal-buttons">
              <button className="modal-btn cancel" onClick={() => setShowWarning(false)}>
                Review Answers
              </button>
              <button className="modal-btn confirm" onClick={handleSubmitExam}>
                Submit Exam
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelWarning && (
        <div className="modal-overlay" onClick={() => setShowCancelWarning(false)}>
          <div className="modal cancel-modal" onClick={(e) => e.stopPropagation()}>
            <h3>🚫 Cancel Exam?</h3>
            <p>
              Are you sure you want to cancel this exam?
            </p>
            <p className="warning-text">
              All your progress will be lost and this action cannot be undone.
            </p>
            {violations && violations.length > 0 && (
              <p className="violation-warning">
                ⚠️ {violations.length} violation(s) have been recorded during this session.
              </p>
            )}
            <div className="modal-buttons">
              <button className="modal-btn cancel" onClick={() => setShowCancelWarning(false)}>
                Continue Exam
              </button>
              <button className="modal-btn confirm danger" onClick={onEndExam}>
                Cancel Exam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamInterface;
